import React from 'react';
import { Form, Formik } from 'formik';
import './create-account.form.scss';
import { ReferenceDto } from '../../../services/dtos/reference.dto';
import { PasswordCriterionsDto } from '../../../services/dtos/password-criterions.dto';
import { SubscriberAccountDto } from '../../../services/dtos/subscriber-account.dto';
import {
    controlLengthMax,
    controlLengthMin,
    controlLowerCase,
    controlNumeric,
    controlSpecific,
    controlUpperCase,
    verifyAge,
} from '../../../utils/form-validators.utils';
import CreateAccountViewForm from './create-account-view.form';
import { mixed, object, ref, string } from 'yup';
import { Container, Grid, Paper, Typography } from '@mui/material';
import { messageinfosPassword } from '../../../utils/traitment.utils';
import { ERROR_MAIL } from '../../../utils/constants.utils';

//Props d'entrée de la fonction CreateAccountForm
type Props = {
    civilities: ReferenceDto[];
    passwordCriterions: PasswordCriterionsDto | null;
    messagesPassword: string[];
    handlerSubmit: (e: SubscriberAccountDto) => void;
    handlerOnChangePassword: (password: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    isMoral: boolean;
    handleChangeInterlocutor: (interlocutor: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    interlocutorsSelection: { value: string; label: string }[];
    validate: boolean;
};

/**
 * Composant qui permet de gérer le formulaire de cration de compte
 * @param civilities liste des civilités
 * @param passwordCriterions critères de validation de mot de passe
 * @param handlerOnChangePassword handle sur le onchange pour le mot de passe
 * @param messagesPassword tooltip mot de passe
 * @param handlerSubmit handler submit du formulaire
 * @param isMoral permet de savoir si c'est une PM
 * @param validate permet de savoir si le formulaire et envoyé
 * @param handleChangeInterlocutor handler sur le changement d'interlocuteur
 * @param interlocutorsSelection liste des valeurs d'interlocuteurs
 * @constructor
 */
const CreateAccountForm: React.FC<Props> = ({
    civilities,
    passwordCriterions,
    handlerOnChangePassword,
    messagesPassword,
    handlerSubmit,
    isMoral,
    validate,
    handleChangeInterlocutor,
    interlocutorsSelection,
}) => {
    //Constantes
    const messageCaracteresMin = "Merci d'entrer deux caractères minimum";
    const nbCaracteresMin = 2;
    const nbCaracteresTel = 9;

    // Définition des contrôles de validation du formulaire
    // Pour la partie mot de passe, nous avons plusieurs tests pour avoir des messages différents suivant les cas
    // Les vérifications changent en fonction de si l'interlocuteur est une personne morale ou non
    const validationSchema = object({
        //Controle des champs utilisateurs : obligatoire
        utilisateur: object().shape({
            type: mixed<ReferenceDto>().required("Le type d'interlocuteur est obligatoire"),
            civilite: mixed<ReferenceDto>().when(() =>
                isMoral
                    ? mixed<ReferenceDto>().notRequired()
                    : mixed<ReferenceDto>().required('La civilité est obligatoire')
            ),
            nomUsage: string().when(() =>
                isMoral
                    ? string().trim().required('Le nom est obligatoire').min(nbCaracteresMin, messageCaracteresMin)
                    : string()
                          .trim()
                          .required('Le nom est obligatoire')
                          .matches(
                              /^[A-Za-z-áàâäãåçéèêëíìîïñóòôöõúùûüýÿæœÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕÚÙÛÜÝŸÆŒ ]+$/,
                              "Seul les caractères alphabétiques, le tiret (-) et l'apostrophe (') sont autorisés"
                          )
                          .min(nbCaracteresMin, messageCaracteresMin)
            ),
            nomNaissance: string()
                .trim()
                .matches(
                    /^[A-Za-z-áàâäãåçéèêëíìîïñóòôöõúùûüýÿæœÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕÚÙÛÜÝŸÆŒ ]+$/,
                    "Seul les caractères alphabétiques, le tiret (-) et l'apostrophe (') sont autorisés"
                )
                .min(nbCaracteresMin, messageCaracteresMin),
            prenom: string().when(() =>
                isMoral
                    ? string().notRequired()
                    : string()
                          .trim()
                          .matches(
                              /^[A-Za-z-áàâäãåçéèêëíìîïñóòôöõúùûüýÿæœÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕÚÙÛÜÝŸÆŒ ]+$/,
                              "Seul les caractères alphabétiques, le tiret - et l'apostrophe ' sont autorisés"
                          )
                          .min(nbCaracteresMin, messageCaracteresMin)
                          .required('Le prénom est obligatoire')
            ),
            dateNaissance: string().when(() =>
                isMoral
                    ? string().notRequired()
                    : string()
                          .required('La date de naissance est obligatoire')
                          .test('age', 'Vous devez avoir au moins 16 ans pour créer un compte', value =>
                              verifyAge(value)
                          )
            ),
            //Controle du champ tel portable
            //Controle : obligatoire / taille / format
            telPort: string()
                .when('telFixe', {
                    is: (telFixe: string) => !telFixe,
                    then: schema => schema.required('Un des deux téléphones doit-être renseigné'),
                })
                .matches(/^\d+$/, {
                    message: 'Caractère non valide inséré.',
                })
                .length(nbCaracteresTel, "Merci d'entrer 9 caractères."),
            //Controle du champ tel fixe
            //Controle : obligatoire / taille / format
            telFixe: string()
                .matches(/^\d+$/, {
                    message: 'Caractère non valide inséré.',
                })
                .length(nbCaracteresTel, "Merci d'entrer 9 caractères."),
            //Controle du champ mail
            //Controle : obligatoire / taille / format
            mailPerso: string()
                .email(ERROR_MAIL)
                .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, ERROR_MAIL),
            mailPro: string()
                .email(ERROR_MAIL)
                .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, ERROR_MAIL),
        }),
        //Controle du champ Identifiant
        //Controle : obligatoire / taille / format
        identifiant: string()
            .email(ERROR_MAIL)
            .required("L'identifiant est obligatoire")
            .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, ERROR_MAIL),
        //Contrôle du champ mot de passe
        //Contrôle : obligatoire / règles en fonction des critères récupérée
        motDePasse: string()
            .trim()
            .required('Le mot de passe est obligatoire')
            .test('upper-case', 'Le mot de passe doit contenir au moins une lettre en majuscules', value =>
                controlUpperCase(value, passwordCriterions?.upperCase)
            )
            .test('lower-case', 'Le mot de passe doit contenir au moins une lettre en minuscules', value =>
                controlLowerCase(value, passwordCriterions?.lowerCase)
            )
            .test('number', 'Le mot de passe doit contenir au moins un chiffre', value =>
                controlNumeric(value, passwordCriterions?.number)
            )
            .test('symbole', 'Le mot de passe doit contenir au moins un caractère spécial : !@#%&', value =>
                controlSpecific(value, passwordCriterions?.specialChars)
            )
            .test(
                'lengthMin',
                'Le mot de passe doit contenir au moins ' + passwordCriterions?.minLength + ' caractères',
                value => controlLengthMin(value, passwordCriterions?.minLength)
            )
            .test(
                'lengthMax',
                'Le mot de passe avoir au maximum ' + passwordCriterions?.maxLength + ' caractères',
                value => controlLengthMax(value, passwordCriterions?.maxLength)
            ),
        //Controle confirmation de mot de passe
        confirmationMotDePasse: string()
            .trim()
            .required('La confirmation du mot de passe est obligatoire')
            .oneOf([ref('motDePasse')], 'Les mots de passe ne correspondent pas'),
    });

    //Initialisation de valeurs par defauts
    const iniValues = {
        utilisateur: {
            nomNaissance: '',
            nomUsage: '',
            prenom: '',
            type: '',
            civilite: '',
            telPort: '',
            telFixe: '',
            mailPerso: '',
            mailPro: '',
            dateNaissance: '',
        },
        motDePasse: '',
        confirmationMotDePasse: '',
        identifiant: '',
        urlValidation: '',
    };

    //Message tooltip mail
    const infosMailPerso = (
        <>
            <Typography variant="caption" display="block">
                Si l'email personnel n'est pas renseigné, l'identifiant sera utilisé comme adresse mail personnelle.
            </Typography>
        </>
    );
    //Message tooltip identifiant
    const infosIdentifiant = (
        <>
            <Typography variant="caption" display="block">
                Votre identifiant doit être une adresse e-mail valide
            </Typography>
        </>
    );

    return (
        //Séparation de la vue
        <Container className="container-register">
            <Typography variant={'h4'}>Créer mon espace usagers</Typography>
            <Container maxWidth="md">
                <Paper>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography variant={'h5'}>Vos coordonnées</Typography>
                        </Grid>
                        <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                            <Typography variant={'subtitle2'}>* : champs obligatoires</Typography>
                        </Grid>
                    </Grid>
                    {/*init du formulaire*/}
                    <Formik initialValues={iniValues} onSubmit={handlerSubmit} validationSchema={validationSchema}>
                        <Form>
                            {/*Appel du vue qui ne fait qu'afficher les champs du formulaire*/}
                            <CreateAccountViewForm
                                handlerOnChangePassword={handlerOnChangePassword}
                                civilities={civilities}
                                infosPassword={messageinfosPassword(messagesPassword)}
                                infosMailPerso={infosMailPerso}
                                isMoral={isMoral}
                                validate={validate}
                                handleChangeInterlocutor={handleChangeInterlocutor}
                                interlocutorsSelection={interlocutorsSelection}
                                infosIdentifiant={infosIdentifiant}
                            />
                        </Form>
                    </Formik>
                </Paper>
            </Container>
        </Container>
    );
};

export default CreateAccountForm;
